import React, { FC, useEffect, useMemo } from "react";
import Glide from "@glidejs/glide";
import Heading from "components/Heading/Heading";
import { TaxonomyType } from "data/types";
import CardCategory3 from "components/CardCategory3/CardCategory3";
import CardCategory4 from "components/CardCategory4/CardCategory4";
import CardCategory5 from "components/CardCategory5/CardCategory5";
import useNcId from "hooks/useNcId";
import NextPrev from "shared/NextPrev/NextPrev";

export interface SectionSliderNewCategoriesProps {
  className?: string;
  itemClassName?: string;
  heading?: string;
  subHeading?: string;
  categories?: TaxonomyType[];
  categoryCardType?: "card3" | "card4" | "card5";
  itemPerRow?: 4 | 5;
  sliderStyle?: "style1" | "style2";
  uniqueClassName: string;
}

const SectionSliderNewCategories: FC<SectionSliderNewCategoriesProps> = ({
  heading = "Product Categories",
  subHeading = "Our expanded product range is designed with precision to address the specific needs of engineers.",
  className = "",
  categories = [],
  itemPerRow = 5,
  categoryCardType = "card3",
  sliderStyle = "style1",
  uniqueClassName,
}) => {
  const UNIQUE_CLASS = "SectionSliderNewCategories__" + uniqueClassName + useNcId();

  const glide = useMemo(() => {
    return new Glide(`.${UNIQUE_CLASS}`, {
      type: "carousel",
      perView: itemPerRow,
      gap: 20,
      bound: true,
      autoplay: 3500,
      hoverpause: true,
      animationDuration: 600,
      breakpoints: {
        1280: { perView: Math.min(itemPerRow, 4), gap: 20 },
        1024: { perView: 3, gap: 16 },
        768: { perView: 2, gap: 16 },
        480: { perView: 1.3, gap: 12 },
      },
    });
  }, [UNIQUE_CLASS, itemPerRow]);

  useEffect(() => {
    if (categories.length === 0) return;
    const timer = setTimeout(() => glide.mount(), 80);
    return () => {
      clearTimeout(timer);
      try { glide.destroy(); } catch (_) {}
    };
  }, [glide, UNIQUE_CLASS, categories.length]);

  const renderCard = (item: TaxonomyType) => {
    switch (categoryCardType) {
      case "card4":
        return <CardCategory4 taxonomy={item} />;
      case "card5":
        return <CardCategory5 taxonomy={item} />;
      default:
        return <CardCategory3 taxonomy={item} />;
    }
  };

  if (categories.length === 0) return null;

  return (
    <div className={`nc-SectionSliderNewCategories ${className}`}>
      {heading && (
        <Heading
          desc={subHeading}
          isCenter={sliderStyle === "style2"}
        >
          {heading}
        </Heading>
      )}

      <div className={`${UNIQUE_CLASS} relative group mt-8`}>
        <div className="glide__track" data-glide-el="track">
          <ul className="glide__slides !overflow-visible">
            {categories.map((item, index) => (
              <li key={item.id || index} className="glide__slide h-auto">
                <div className="px-1 pb-2">
                  {renderCard(item)}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {categories.length > 1 && (
          <>
            <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex top-[40%] -translate-y-1/2 inset-x-0 justify-between pointer-events-none px-0 sm:-mx-4">
              <NextPrev
                className="w-full justify-between pointer-events-auto"
                btnClassName="w-9 h-9 sm:w-10 sm:h-10 shadow-bcr bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700"
              />
            </div>
            <div
              className="glide__bullets flex items-center justify-center mt-6 gap-2"
              data-glide-el="controls[nav]"
            >
              {categories.map((_, i) => (
                <button
                  key={i}
                  className="glide__bullet w-2 h-2 rounded-full bg-neutral-300 dark:bg-neutral-600 transition-all"
                  data-glide-dir={`=${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SectionSliderNewCategories;
